/**
 * SystemPerformancePanel - Realm-wide performance monitoring for Analytics Dashboard
 * 
 * Displays real-time system performance metrics including:
 * - HarfBuzz text shaping performance
 * - Redis cache performance 
 * - Font processing metrics
 * - System health indicators
 * 
 * Features:
 * - Beautiful dark/light mode support
 * - Real-time updates
 * - Responsive design
 * - Professional analytics UI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChartBarIcon, 
  CpuChipIcon, 
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ServerIcon,
  BoltIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { CacheInspector } from './CacheInspector';

interface RealmMetrics {
  realmId: string;
  cache: {
    totalRequests: number;
    totalHits: number;
    overallHitRate: string;
    l1HitRate: string;
    l2HitRate: string;
    l1Size: number;
    estimatedL2Size: number;
  };
  performance: {
    avgL1Latency: string;
    avgL2Latency: string;
    avgRedisLatency: string;
  };
  redis: {
    totalOperations: number;
    errorRate: string;
    reads: number;
    writes: number;
    readErrors: number;
    writeErrors: number;
  };
  activity: {
    firstRequest: string;
    lastRequest: string;
    uptimeSeconds: number;
  };
}

interface SystemPerformancePanelProps {
  className?: string;
}

export const SystemPerformancePanel: React.FC<SystemPerformancePanelProps> = ({
  className = ''
}) => {
  const [metrics, setMetrics] = useState<RealmMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCacheInspector, setShowCacheInspector] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Get current realm ID from JWT token (similar to LocalDocumentEngine)
  const getCurrentRealmId = useCallback((): string | null => {
    try {
      // Try multiple token storage locations
      const token = localStorage.getItem('kc-token') || 
                   sessionStorage.getItem('kc-token') ||
                   localStorage.getItem('token') ||
                   sessionStorage.getItem('token');
      
      if (!token) {
        console.warn('[SystemPerformancePanel] No JWT token found in storage');
        return null;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('[SystemPerformancePanel] JWT payload:', { iss: payload.iss, sub: payload.sub });
      
      const realmMatch = payload.iss?.match(/\/realms\/([^\/]+)$/);
      const realmId = realmMatch ? realmMatch[1] : null;
      
      console.log('[SystemPerformancePanel] Extracted realm ID:', realmId);
      return realmId;
    } catch (error) {
      console.error('[SystemPerformancePanel] Error extracting realm ID:', error);
      return null;
    }
  }, []);

  // Fetch realm-specific performance metrics
  const fetchMetrics = useCallback(async () => {
    let realmId = getCurrentRealmId();
    
    // Fallback to known realm ID for testing
    if (!realmId) {
      console.warn('[SystemPerformancePanel] Using fallback realm ID');
      realmId = 'edgelexlaw-realm'; // Fallback for testing
    }
    
    if (!realmId) {
      setError('Unable to determine realm ID');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await fetch('http://localhost:8081/metrics/realm', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-realm-id': realmId
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMetrics(data.metrics);
        setLastUpdate(new Date());
        console.log('[SystemPerformancePanel] ✅ Fetched realm metrics:', data.metrics);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('[SystemPerformancePanel] Failed to fetch metrics:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getCurrentRealmId]);

  // Auto-refresh metrics
  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  // Format uptime
  const formatUptime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  // Get status color based on performance
  const getStatusColor = (hitRate: string): string => {
    const rate = parseFloat(hitRate.replace('%', ''));
    if (rate >= 95) return 'text-green-600 dark:text-green-400';
    if (rate >= 85) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Get status icon based on performance
  const getStatusIcon = (hitRate: string) => {
    const rate = parseFloat(hitRate.replace('%', ''));
    if (rate >= 95) return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
  };

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-lg"></div>
            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-48"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
        <div className="flex items-center space-x-3 mb-4">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            System Performance Unavailable
          </h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={fetchMetrics}
          className="inline-flex items-center px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-lex-accent-soft rounded-lg">
              <ServerIcon className="w-6 h-6 text-lex-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                System Performance
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Real-time metrics for {metrics.realmId}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {lastUpdate && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Updated {lastUpdate.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => setShowCacheInspector(!showCacheInspector)}
              className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${
                showCacheInspector ? 'bg-lex-accent-soft' : ''
              }`}
              title="Cache Inspector"
            >
              <MagnifyingGlassIcon className={`w-5 h-5 ${
                showCacheInspector ? 'text-lex-accent' : 'text-gray-500'
              }`} />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Toggle details"
            >
              {isExpanded ? (
                <EyeSlashIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <EyeIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <button
              onClick={fetchMetrics}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh metrics"
            >
              <ArrowPathIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Cache Performance */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <BoltIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-800 dark:text-green-300">Cache Performance</span>
              </div>
              {getStatusIcon(metrics.cache.overallHitRate)}
            </div>
            <div className="space-y-1">
              <div className={`text-2xl font-bold ${getStatusColor(metrics.cache.overallHitRate)}`}>
                {metrics.cache.overallHitRate}
              </div>
              <div className="text-xs text-green-700 dark:text-green-400">
                {metrics.cache.totalRequests.toLocaleString()} total requests
              </div>
              <div className="text-xs text-green-600 dark:text-green-500">
                L1: {metrics.cache.l1HitRate} • L2: {metrics.cache.l2HitRate}
              </div>
            </div>
          </div>

          {/* Response Time */}
          <div className="bg-lex-accent-soft rounded-lg p-4 border border-lex-accent">
            <div className="flex items-center space-x-2 mb-2">
              <ClockIcon className="w-5 h-5 text-lex-accent" />
              <span className="text-sm font-medium text-lex-accent">Response Time</span>
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-bold text-lex-accent">
                {metrics.performance.avgL2Latency}
              </div>
              <div className="text-xs text-lex-accent">
                Average processing time
              </div>
              <div className="text-xs text-lex-accent">
                Redis: {metrics.performance.avgRedisLatency}
              </div>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center space-x-2 mb-2">
              <CpuChipIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-800 dark:text-purple-300">System Health</span>
            </div>
            <div className="space-y-1">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatUptime(metrics.activity.uptimeSeconds)}
              </div>
              <div className="text-xs text-purple-700 dark:text-purple-400">
                Uptime
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-500">
                Error rate: {metrics.redis.errorRate}
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Metrics (Expandable) */}
        {isExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Detailed Metrics</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Redis Operations */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Redis Operations</h5>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total Operations:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{metrics.redis.totalOperations.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Reads:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{metrics.redis.reads.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Writes:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{metrics.redis.writes.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Errors:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {(metrics.redis.readErrors + metrics.redis.writeErrors).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Cache Details */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Cache Details</h5>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total Hits:</span>
                    <span className="font-medium text-green-600 dark:text-green-400">{metrics.cache.totalHits.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">L1 Cache Size:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{metrics.cache.l1Size.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">L2 Cache Size:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{metrics.cache.estimatedL2Size.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Last Activity:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(metrics.activity.lastRequest).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cache Inspector Section */}
        {showCacheInspector && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <CacheInspector />
          </div>
        )}
      </div>
    </div>
  );
};
